import { mqttLogger } from '@hooks/mqtt/utils';
import { Getter, Setter, WritableAtom } from 'jotai';
import { manageListenerLifecycleAtom } from '../message/mqttListenerAtom';
import { unsubscribeFromTopicAtom } from '../subscription/unsubscribeFromTopicAtom';
import { PendingUnsubscribe } from '../types';
import { clearExistingTimeout } from './debounceUtils';

export function addPendingUnsubscribe(
  key: string,
  topic: string,
  subscriberId: string,
  pendingMap: Map<string, PendingUnsubscribe>,
): Map<string, PendingUnsubscribe> {
  const newPending = new Map(pendingMap);
  newPending.set(key, {
    topic,
    subscriberId,
    timestamp: Date.now(),
  });
  return newPending;
}

export function removePendingUnsubscribe(
  key: string,
  pendingMap: Map<string, PendingUnsubscribe>,
): Map<string, PendingUnsubscribe> {
  const newPending = new Map(pendingMap);
  newPending.delete(key);
  return newPending;
}

export async function executeForceUnsubscribe(
  set: Setter,
  topic: string,
  subscriberId: string,
): Promise<void> {
  mqttLogger.debug(
    `Force unsubscribing from ${topic} (subscriber: ${subscriberId})`,
  );
  await set(unsubscribeFromTopicAtom, { topic, subscriberId });
  set(manageListenerLifecycleAtom, 'decrement');
}

export async function executeDebouncedUnsubscribe(
  get: Getter,
  set: Setter,
  key: string,
  topic: string,
  subscriberId: string,
  pendingUnsubscribesAtom: WritableAtom<
    Map<string, PendingUnsubscribe>,
    [Map<string, PendingUnsubscribe>],
    void
  >,
  pendingTimeoutsAtom: WritableAtom<
    Map<string, NodeJS.Timeout>,
    [Map<string, NodeJS.Timeout>],
    void
  >,
): Promise<void> {
  const stillPending = get(pendingUnsubscribesAtom);
  if (!stillPending.has(key)) {
    return;
  }

  mqttLogger.debug(
    `Executing debounced unsubscribe for ${topic} (subscriber: ${subscriberId})`,
  );

  await set(unsubscribeFromTopicAtom, { topic, subscriberId });
  set(manageListenerLifecycleAtom, 'decrement');

  const updatedPending = removePendingUnsubscribe(key, stillPending);
  set(pendingUnsubscribesAtom, updatedPending);

  const timeouts = get(pendingTimeoutsAtom);
  const updatedTimeouts = clearExistingTimeout(key, timeouts);
  set(pendingTimeoutsAtom, updatedTimeouts);
}

export function logScheduledUnsubscribe(
  topic: string,
  subscriberId: string,
  delay: number,
): void {
  mqttLogger.debug(
    `Scheduled unsubscribe for ${topic} (subscriber: ${subscriberId}) with ${delay}ms delay`,
  );
}

export function logCancelledUnsubscribe(
  topic: string,
  subscriberId: string,
): void {
  mqttLogger.debug(
    `Cancelled pending unsubscribe for ${topic} (subscriber: ${subscriberId})`,
  );
}

export function logFlushingUnsubscribes(count: number): void {
  mqttLogger.info(`Flushing ${count} pending unsubscriptions`);
}

export async function flushAllPendingUnsubscribes(
  set: Setter,
  pendingUnsubscribes: Map<string, PendingUnsubscribe>,
): Promise<void> {
  const unsubscribePromises = Array.from(pendingUnsubscribes.values()).map(
    (pending) =>
      set(unsubscribeFromTopicAtom, {
        topic: pending.topic,
        subscriberId: pending.subscriberId,
      }),
  );

  await Promise.all(unsubscribePromises);
}
